import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import api from "../utils/api";
import { useAuth } from "../AuthContext";
import "./ReportButton.css";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const REASONS = [
  ["SPAM", "Spam or scam"],
  ["HARASSMENT", "Harassment or bullying"],
  ["HATE", "Hate speech"],
  ["SEXUAL", "Sexual or explicit content"],
  ["VIOLENCE", "Violence or dangerous content"],
  ["CHILD_SAFETY", "Child safety"],
  ["COPYRIGHT", "Copyright infringement (my work was copied)"],
  ["PRIVACY", "Shares someone's private information"],
  ["MISINFORMATION", "False or misleading"],
  ["OTHER", "Something else"],
];

/**
 * "Report" link + dialog for content, comments, messages and profiles (India IT Rules 2021
 * grievance mechanism). Signed-in users report directly; guests give an email and pass reCAPTCHA.
 * Props: targetType (BOOK|ARTICLE|RECIPE|PODCAST|GALLERY|GALLERY_IMAGE|COMMENT|MESSAGE|PROFILE),
 * targetId, targetUserId (the person who posted it), targetTitle, label, className.
 */
export default function ReportButton({ targetType, targetId, targetUserId, targetTitle, label = "Report", className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const captchaRef = useRef(null);

  // Nobody needs to report their own content.
  if (user && targetUserId != null && Number(targetUserId) === Number(user.userId)) return null;

  const close = () => {
    setOpen(false);
    setReason(""); setDetails(""); setError(""); setDone(""); setCaptcha("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!reason) { setError("Please choose a reason."); return; }
    if (reason === "OTHER" && !details.trim()) { setError("Please tell us what's wrong."); return; }
    if (!user) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError("Please enter your email so we can update you."); return; }
      if (RECAPTCHA_SITE_KEY && !captcha) { setError("Please complete the reCAPTCHA."); return; }
    }
    setSending(true);
    try {
      const r = await api.post("/api/reports", {
        targetType, targetId, targetUserId, targetTitle,
        targetUrl: window.location.pathname + window.location.search,
        reason, details: details.trim(),
        name: user ? undefined : name.trim(),
        email: user ? undefined : email.trim(),
        recaptchaToken: user ? undefined : captcha,
      });
      setDone(r.data?.message || "Thank you. Your report has been received.");
    } catch (err) {
      setError(err.response?.data?.error || "Could not send your report. Please try again.");
      setCaptcha("");
      captchaRef.current?.reset();
    }
    setSending(false);
  };

  return (
    <>
      <button type="button" className={`report-btn ${className}`} onClick={() => setOpen(true)}>
        {label}
      </button>
      {open && (
        <div className="report-overlay" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
          <div className="report-modal" role="dialog" aria-modal="true" aria-labelledby="report-title">
            <button type="button" className="report-close" aria-label="Close" onClick={close}>×</button>
            <h2 id="report-title">Report{targetTitle ? `: ${targetTitle}` : ""}</h2>
            {done ? (
              <>
                <p className="report-done" role="status">{done}</p>
                <button type="button" className="report-submit" onClick={close}>Close</button>
              </>
            ) : (
              <form onSubmit={submit}>
                <p className="report-intro">Tell us what's wrong. Reports go to SaatSaheli's moderators, not to the person you're reporting.</p>
                <fieldset className="report-reasons">
                  <legend>Reason</legend>
                  {REASONS.map(([value, text]) => (
                    <label key={value}>
                      <input type="radio" name="report-reason" value={value} checked={reason === value}
                        onChange={() => { setReason(value); setError(""); }} />
                      <span>{text}</span>
                    </label>
                  ))}
                </fieldset>
                <label className="report-field">
                  Details {reason === "OTHER" ? "(required)" : "(optional)"}
                  <textarea rows={3} maxLength={2000} value={details} onChange={(e) => setDetails(e.target.value)} />
                </label>
                {!user && (
                  <>
                    <label className="report-field">
                      Your name (optional)
                      <input type="text" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
                    </label>
                    <label className="report-field">
                      Your email (so we can update you)
                      <input type="email" maxLength={255} value={email} onChange={(e) => setEmail(e.target.value)} />
                    </label>
                    {RECAPTCHA_SITE_KEY && (
                      <ReCAPTCHA ref={captchaRef} sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(t) => setCaptcha(t || "")} onExpired={() => setCaptcha("")} />
                    )}
                  </>
                )}
                {reason === "COPYRIGHT" && (
                  <p className="report-note">For a formal copyright notice, see our <a href="/copyright" target="_blank" rel="noopener noreferrer">copyright complaints page</a>.</p>
                )}
                {error && <p className="report-error" role="alert">{error}</p>}
                <button type="submit" className="report-submit" disabled={sending}>{sending ? "Sending…" : "Send report"}</button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
